import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Grid, Tab, Tabs, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Typography, Tooltip, SvgIcon, useTheme, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Edit05, Trash01 } from '../../../assets';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../components/shared/useUser';
import { PricingModelResponse, DeliveryPricingRuleResponse, DateOnly, RetailerHeaderModel, UserFranchiseList } from '../../../app/services/api/ofdClient';
import { client } from '../../../app/services/api/orderManagementClient';
import useNswagClient from '../../../hooks/api/useNswagClient';
import PricingModelEditor from './PricingModelEditor';
import RuleEditor from './RuleEditor';
import PriceCalculator from './PriceCalculator';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import CustomPagination from '../../../components/forms/Pagination';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { TableLoading } from '../../../components/forms/TableLoading';
import { formatDate } from '../../../utils';

const formatDateOnly = (d?: DateOnly | string): string => {
  if (!d) return '';

  if (typeof d === 'string') {
    const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return formatDate(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    }

    return d;
  }

  return formatDate(new Date(d.year ?? 0, (d.month ?? 1) - 1, d.day ?? 1));
};

const PRICING_TAB_MODELS = 0;
const PRICING_TAB_RULES = 1;

const MODEL_TYPE_LABELS: Record<number, { key: string; fallback: string }> = {
  1: { key: 'modelTypeFixed', fallback: 'Fixed' },
  2: { key: 'modelTypeBreak', fallback: 'Break' },
  3: { key: 'modelTypeCumulative', fallback: 'Cumulative' },
};

const RATE_TYPE_LABELS: Record<number, { key: string; fallback: string }> = {
  1: { key: 'rateTypeFixedPrice', fallback: 'Fixed Price' },
  2: { key: 'rateTypeDiscountPercent', fallback: 'Discount %' },
};

const normalizeInfinityLabel = (label: string): string => {
  const normalized = label.trim().toLowerCase();
  if (!normalized || normalized === 'inf' || normalized === 'infinity') {
    return '∞';
  }

  return label;
};

const toQuantityLabel = (value: number | string | null | undefined, translate: (key: string, fallback: string) => string): string => {
  const infinityLabel = normalizeInfinityLabel(translate('modelEditor.infinity', '∞'));
  if (value == null) {
    return infinityLabel;
  }

  const normalized = value.toString().trim().toLowerCase();
  if (!normalized || normalized === 'inf' || normalized === 'infinity' || normalized === '∞') {
    return infinityLabel;
  }

  return value.toString();
};

const formatModelDetails = (model: PricingModelResponse, translate: (key: string, fallback: string) => string): string => {
  const orderedTiers = [...(model.tiers ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return orderedTiers
    .map((tier) => {
      const toLabel = toQuantityLabel(tier.toQuantity, translate);
      const rateSuffix = model.rateType === 2 ? '%' : '';
      return `${tier.fromQuantity}-${toLabel}: ${tier.rate}${rateSuffix}`;
    })
    .join(', ');
};

const DeliveryPricingPage = () => {
  const { t } = useTranslation('deliveryPricing');
  const theme = useTheme();
  const { hasPermissionTo, user, selectedFranchise, selectedStore } = useContext(UserContext);
  const canWrite = hasPermissionTo(['DeliveryPricingWrite']);
  const { deliveryPricingClient, pricingModelsClient, getRetailersList } = useNswagClient();
  const deliveryPricingClientRef = useRef(deliveryPricingClient);
  const pricingModelsClientRef = useRef(pricingModelsClient);
  const getRetailersListRef = useRef(getRetailersList);

  useEffect(() => {
    deliveryPricingClientRef.current = deliveryPricingClient;
    pricingModelsClientRef.current = pricingModelsClient;
    getRetailersListRef.current = getRetailersList;
  }, [deliveryPricingClient, pricingModelsClient, getRetailersList]);

  const [activeTab, setActiveTab] = useState(PRICING_TAB_MODELS);
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState<PricingModelResponse[]>([]);
  const [rules, setRules] = useState<DeliveryPricingRuleResponse[]>([]);
  const [retailerNamesById, setRetailerNamesById] = useState<Record<number, string>>({});
  const [shippingMethodNamesById, setShippingMethodNamesById] = useState<Record<number, string>>({});
  const [modelsPage, setModelsPage] = useState(0);
  const [modelsPageSize] = useState(10);
  const [rulesPage, setRulesPage] = useState(0);
  const [rulesPageSize] = useState(10);
  const [rulesTotalCount, setRulesTotalCount] = useState(0);

  const [editingModel, setEditingModel] = useState<PricingModelResponse | null>(null);
  const [showModelEditor, setShowModelEditor] = useState(false);
  const [editingRule, setEditingRule] = useState<DeliveryPricingRuleResponse | null>(null);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{ type: 'model' | 'rule'; id: number } | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const modelsMaxPages = Math.max(1, Math.ceil(models.length / modelsPageSize));
  const visibleModels = models.slice(modelsPage * modelsPageSize, modelsPage * modelsPageSize + modelsPageSize);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      const data = await pricingModelsClientRef.current.pricingModelsAll();
      setModels(data);
    } catch {
      setSnackbar({ open: true, message: t('messages.loadModelsFailed', 'Failed to load pricing models'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await deliveryPricingClientRef.current.rulesGET(undefined, false, rulesPage + 1, rulesPageSize);
      setRules(data.items ?? []);
      setRulesTotalCount(data.totalCount ?? 0);
    } catch {
      setSnackbar({ open: true, message: t('messages.loadRulesFailed', 'Failed to load pricing rules'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t, rulesPage, rulesPageSize]);

  const loadRetailers = useCallback(async () => {
    const userFranchiseList: UserFranchiseList = {
      franchiseNames: (user?.franchises?.map(f => f.franchiseName).filter((name): name is string => !!name)) ?? [],
      otherRegions: false,
      currentFranchiseName: selectedFranchise?.franchiseName ?? '',
    };

    try {
      const result = await getRetailersListRef.current(userFranchiseList);
      const retailers = result.retailerList ?? [];

      const next: Record<number, string> = {};
      retailers.forEach((retailer: RetailerHeaderModel) => {
        if (retailer.retailerId) {
          next[retailer.retailerId] = retailer.name?.trim() ?? '';
        }
      });
      setRetailerNamesById(next);
    } catch {
      setRetailerNamesById({});
    }
  }, [user, selectedFranchise]);

  const loadShippingMethods = useCallback(async () => {
    try {
      const storeNumber = selectedStore?.storeNumber?.toString();
      const result = await client.getOnboardingDeliveryMethods(storeNumber);
      const methods = result?.data?.ShippingMethodList ?? [];

      const next: Record<number, string> = {};
      methods.forEach((method) => {
        next[method.ShippingMethodId] = method.Name;
      });
      setShippingMethodNamesById(next);
    } catch {
      setShippingMethodNamesById({});
    }
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    if (activeTab === PRICING_TAB_MODELS) {
      loadModels();
      return;
    }

    if (models.length === 0) {
      loadModels();
    }
    loadShippingMethods();
    loadRetailers();
    loadRules();
  }, [activeTab, loadModels, loadRules, loadRetailers, loadShippingMethods, models.length]);

  useEffect(() => {
    if (modelsPage >= modelsMaxPages) {
      setModelsPage(Math.max(0, modelsMaxPages - 1));
    }
  }, [modelsPage, modelsMaxPages]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'model') {
        await pricingModelsClientRef.current.pricingModelsDELETE(deleteTarget.id);
        setModels(prev => prev.filter(m => m.pricingModelId !== deleteTarget.id));
      } else {
        await deliveryPricingClientRef.current.rulesDELETE(deleteTarget.id);
        setRules(prev => prev.filter(r => r.deliveryPricingRuleId !== deleteTarget.id));
      }
      setSnackbar({ open: true, message: t('messages.deleted', 'Deleted successfully'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('messages.deleteFailed', 'Delete failed'), severity: 'error' });
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleModelSaved = () => {
    setShowModelEditor(false);
    setEditingModel(null);
    loadModels();
    setSnackbar({ open: true, message: t('messages.modelSaved', 'Pricing model saved'), severity: 'success' });
  };

  const handleRuleSaved = () => {
    setShowRuleEditor(false);
    setEditingRule(null);
    loadRules();
    setSnackbar({ open: true, message: t('messages.ruleSaved', 'Pricing rule saved'), severity: 'success' });
  };

  return (
    <PageArea>
      <Grid container
        spacing={2}>
        <Grid item
          xs={12}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'>
            <PageTitle>{t('title', 'Delivery Pricing')}</PageTitle>
            <Box
              display='flex'
              gap={1}>
              <Button
                variant='secondary'
                onClick={() => setShowCalculator(true)}>
                {t('calculator', 'Price Calculator')}
              </Button>
              {canWrite && (
                <Button
                  variant='primary'
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (activeTab === PRICING_TAB_MODELS) {
                      setEditingModel(null);
                      setShowModelEditor(true);
                    } else {
                      setEditingRule(null);
                      setShowRuleEditor(true);
                    }
                  }}
                >
                  {activeTab === PRICING_TAB_MODELS
                    ? t('addModel', 'Add Model')
                    : t('addRule', 'Add Rule')}
                </Button>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item
          xs={12}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}>
            <Tab
              label={t('modelsTab', 'Pricing Models')}
              value={PRICING_TAB_MODELS}
            />
            <Tab
              label={t('rulesTab', 'Delivery Rules')}
              value={PRICING_TAB_RULES}
            />
          </Tabs>
        </Grid>

        <Grid item
          xs={12}>
          {loading && activeTab === PRICING_TAB_MODELS ? (
            <Box
              display='flex'
              justifyContent='center'
              py={4}>
              <CircularProgress />
            </Box>
          ) : activeTab === PRICING_TAB_MODELS ? (
            <>
              <TableContainer
                component={Paper}
                sx={{ borderRadius: '12px' }}>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableHead-root .MuiTableCell-root': {
                      fontWeight: 600,
                    },
                    '& .MuiTableBody-root .MuiTableCell-root': {
                      typography: 'textSM',
                    },
                  }}>
                  <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                    <TableRow>
                      <TableCell>{t('name', 'Name')}</TableCell>
                      <TableCell>{t('modelType', 'Model Type')}</TableCell>
                      <TableCell>{t('rateType', 'Rate Type')}</TableCell>
                      <TableCell>{t('tiers', 'Tiers')}</TableCell>
                      <TableCell>{t('modelEditor.roundUpThreshold', 'Round Up Threshold %')}</TableCell>
                      <TableCell>{t('details', 'Details')}</TableCell>
                      {canWrite && (
                        <TableCell
                          sx={{ width: 96, whiteSpace: 'nowrap' }}
                          align='right'>
                          {t('actions', 'Actions')}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleModels.map(model => (
                      <TableRow key={model.pricingModelId}>
                        <TableCell>{model.name}</TableCell>
                        <TableCell>
                          {model.modelType != null
                            ? (MODEL_TYPE_LABELS[model.modelType]
                              ? t(MODEL_TYPE_LABELS[model.modelType].key, MODEL_TYPE_LABELS[model.modelType].fallback)
                              : model.modelType)
                            : ''}
                        </TableCell>
                        <TableCell>
                          {model.rateType != null
                            ? (RATE_TYPE_LABELS[model.rateType]
                              ? t(RATE_TYPE_LABELS[model.rateType].key, RATE_TYPE_LABELS[model.rateType].fallback)
                              : model.rateType)
                            : ''}
                        </TableCell>
                        <TableCell>{model.tiers?.length ?? 0}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {model.roundUpThresholdPercent != null ? `${model.roundUpThresholdPercent}%` : t('notSet', '-')}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 1 }}>
                          {formatModelDetails(model, (key, fallback) => t(key, fallback))}
                        </TableCell>
                        {canWrite && (
                          <TableCell
                            align='right'
                            sx={{ width: 96, whiteSpace: 'nowrap' }}>
                            <Box
                              display='flex'
                              justifyContent='flex-end'
                              alignItems='center'
                              gap={0.5}>
                              <Tooltip title={t('edit', 'Edit')}>
                                <IconButton
                                  size='small'
                                  aria-label={t('edit', 'Edit')}
                                  onClick={() => { setEditingModel(model); setShowModelEditor(true); }}>
                                  <SvgIcon
                                    fontSize='small'
                                    sx={{ color: theme.palette.primary[700] }}>
                                    <Edit05 />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('delete', 'Delete')}>
                                <IconButton
                                  size='small'
                                  aria-label={t('delete', 'Delete')}
                                  onClick={() => setDeleteTarget({ type: 'model', id: model.pricingModelId! })}>
                                  <SvgIcon
                                    fontSize='small'
                                    sx={{ color: theme.palette.error[500] }}>
                                    <Trash01 />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {visibleModels.length > 0 && (
                      <EmptyRows
                        numberOfCurrentRows={visibleModels.length}
                        height={40}
                        colSpan={canWrite ? 7 : 6}
                      />
                    )}
                    {models.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={canWrite ? 7 : 6}
                          align='center'>
                          <Typography variant='textSM'
                            color='text.secondary'>
                            {t('noModels', 'No pricing models configured')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                  <CustomPagination
                    useNoDataMessage={false}
                    page={modelsPage}
                    setPage={setModelsPage}
                    maxPages={modelsMaxPages}
                    breakpointForChangeDisplay={120000}
                  />
                </Box>
              </TableContainer>
            </>
          ) : (
            <>
              {loading ? (
                <TableLoading
                  headerHeight={50}
                  bodyRowHeight={40}
                />
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{ borderRadius: '12px' }}>
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        fontWeight: 600,
                      },
                      '& .MuiTableBody-root .MuiTableCell-root': {
                        typography: 'textSM',
                      },
                    }}>
                    <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
                      <TableRow>
                        <TableCell>{t('model', 'Model')}</TableCell>
                        <TableCell>{t('shippingMethod', 'Shipping Method')}</TableCell>
                        <TableCell>{t('scope', 'Scope')}</TableCell>
                        <TableCell>{t('effectiveFrom', 'From')}</TableCell>
                        <TableCell>{t('effectiveTo', 'To')}</TableCell>
                        <TableCell>{t('priority', 'Priority')}</TableCell>
                        <TableCell>{t('status', 'Status')}</TableCell>
                        {canWrite && (
                          <TableCell
                            sx={{ width: 96, whiteSpace: 'nowrap' }}
                            align='right'>
                            {t('actions', 'Actions')}
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rules.map(rule => (
                        <TableRow key={rule.deliveryPricingRuleId}>
                          <TableCell>{rule.pricingModelName}</TableCell>
                          <TableCell>
                            {rule.shippingMethodId != null
                              ? (shippingMethodNamesById[rule.shippingMethodId] ?? rule.shippingMethodId)
                              : t('notSet', '—')}
                          </TableCell>
                          <TableCell>
                            {rule.retailerIds && rule.retailerIds.length > 0
                              ? `${t('scopeRetailerPrefix', 'Retailer')} ${rule.retailerIds.map(id => retailerNamesById[id] ?? id).join(', ')}`
                              : t('scopeGlobal', 'Global')}
                          </TableCell>
                          <TableCell>{formatDateOnly(rule.effectiveFrom)}</TableCell>
                          <TableCell>{rule.effectiveTo ? formatDateOnly(rule.effectiveTo) : t('notSet', '—')}</TableCell>
                          <TableCell>{rule.priority}</TableCell>
                          <TableCell>
                            <Chip
                              label={rule.enabled ? t('enabled', 'Enabled') : t('disabled', 'Disabled')}
                              color={rule.enabled ? 'success' : 'default'}
                              size='small'
                            />
                          </TableCell>
                          {canWrite && (
                            <TableCell
                              align='right'
                              sx={{ width: 96, whiteSpace: 'nowrap' }}>
                              <Box
                                display='flex'
                                justifyContent='flex-end'
                                alignItems='center'
                                gap={0.5}>
                                <Tooltip title={t('edit', 'Edit')}>
                                  <IconButton
                                    size='small'
                                    aria-label={t('edit', 'Edit')}
                                    onClick={() => { setEditingRule(rule); setShowRuleEditor(true); }}>
                                    <SvgIcon
                                      fontSize='small'
                                      sx={{ color: theme.palette.primary[700] }}>
                                      <Edit05 />
                                    </SvgIcon>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('delete', 'Delete')}>
                                  <IconButton
                                    size='small'
                                    aria-label={t('delete', 'Delete')}
                                    onClick={() => setDeleteTarget({ type: 'rule', id: rule.deliveryPricingRuleId! })}>
                                    <SvgIcon
                                      fontSize='small'
                                      sx={{ color: theme.palette.error[500] }}>
                                      <Trash01 />
                                    </SvgIcon>
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      <EmptyRows
                        numberOfCurrentRows={rules.length}
                        height={40}
                        colSpan={canWrite ? 8 : 7}
                      />
                    </TableBody>
                  </Table>
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <CustomPagination
                      useNoDataMessage={false}
                      page={rulesPage}
                      setPage={setRulesPage}
                      maxPages={Math.ceil(rulesTotalCount / rulesPageSize)}
                      breakpointForChangeDisplay={120000}
                    />
                  </Box>
                </TableContainer>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {showModelEditor && (
        <PricingModelEditor
          model={editingModel}
          open={showModelEditor}
          onClose={() => { setShowModelEditor(false); setEditingModel(null); }}
          onSaved={handleModelSaved}
        />
      )}

      {showRuleEditor && (
        <RuleEditor
          rule={editingRule}
          models={models}
          open={showRuleEditor}
          onClose={() => { setShowRuleEditor(false); setEditingRule(null); }}
          onSaved={handleRuleSaved}
        />
      )}

      {showCalculator && (
        <PriceCalculator
          open={showCalculator}
          onClose={() => setShowCalculator(false)}
        />
      )}

      <ConfirmationDialog
        open={!!deleteTarget}
        icon={<WarningAmberIcon />}
        title={t('confirmDelete', 'Confirm Delete')}
        message={deleteTarget?.type === 'model'
          ? t(
            'confirmDeleteModelMessage',
            'Deleting this pricing model will also delete linked delivery rules. This action cannot be undone.',
          )
          : t('confirmDeleteMessage', 'Are you sure you want to delete this item? This action cannot be undone.')}
        confirmButtonText={t('delete', 'Delete')}
        cancelButtonText={t('cancel', 'Cancel')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      <MessagesSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
        duration={3000}
      />
    </PageArea>
  );
};

export default DeliveryPricingPage;
